import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cities from "../../data/cities.json";
import { getCityByCode } from "../../utils/flightSearch";

// Add custom styles for cross-browser select option hover and tooltip bullet points
const selectStyles = `
  select option:hover {
    background-color: #00467f !important;
    color: white !important;
  }
  select option:checked {
    background-color: #00467f !important;
    color: white !important;
  }
  /* WebKit specific styles */
  @media screen and (-webkit-min-device-pixel-ratio:0) {
    select option:hover {
      background-color: #00467f !important;
      color: white !important;
    }
  }
  /* Edge specific styles */
  @supports (-ms-ime-align: auto) {
    select option:hover {
      background-color: #00467f !important;
      color: white !important;
    }
  }
  
  /* Material Design Tooltip bullet styles */
  .tooltip-heading,
  .tool-tip,
  p.tooltip-heading,
  p.tool-tip {
    font-family: "AmericanSans Var", Roboto, sans-serif !important;
    font-weight: 500 !important;
    margin-bottom: 8px !important;
    padding-left: 0 !important;
    margin-left: 4px!important;
    text-align: left !important;
    color: rgba(0, 0, 0, 0.87) !important;
    font-size: 14px !important;
    display: block !important;
  }
  
  .tool-tip-text.list-bullet {
    position: relative !important;
    padding-left: 10px !important;
    list-style: none !important;
    margin-bottom: 8px !important;
    font-family: "AmericanSans Var", Roboto, sans-serif !important;
    color: rgba(0, 0, 0, 0.6) !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
  }
  
  .tool-tip-text.list-bullet::before {
    content: "•" !important;
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    font-weight: normal !important;
    line-height: 1.4 !important;
    font-size: 12px !important;
  }
  
  ul:has(.list-bullet) {
    list-style: none !important;
    margin: 0 !important;
    padding: 0 !important;
    font-family: inherit !important;
  }
  
  /* Override any Material Design list styles */
  .aileron-tooltip ul,
  .aileron-tooltip__body ul {
    list-style: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .aileron-tooltip li,
  .aileron-tooltip__body li {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Ensure tooltip container has proper styling */
  .aileron-tooltip,
  .aileron-tooltip__body {
    font-family: "AmericanSans Var", Roboto, sans-serif !important;
    background-color: white !important;
    border-radius: 4px !important;
    box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, .2), 0px 6px 10px 0px rgba(0, 0, 0, .14), 0px 1px 18px 0px rgba(0, 0, 0, .12) !important;
    padding: 12px 16px !important;
  }
  
  /* Custom tooltip styling for hardcoded tooltip */
  .rounded-xl.shadow-lg.bg-white p.font-semibold,
  .bg-white p.font-semibold {
    text-align: left !important;
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
  
  .rounded-xl.shadow-lg.bg-white,
  .bg-white .font-semibold {
    text-align: left !important;
  }
  
  /* Confirmation code tooltip styling */
  .rounded-lg.shadow-lg.bg-white p.font-semibold,
  .rounded-lg.shadow-lg.bg-white p {
    text-align: left !important;
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
  
  .rounded-lg.shadow-lg.bg-white {
    text-align: left !important;
  }
  
  .rounded-lg.shadow-lg.bg-white .text-sm {
    text-align: left !important;
  }
  
  /* Last name tooltip specific styling for left alignment */
  .rounded.shadow-md.bg-white,
  .rounded.shadow-md.bg-white .text-xs,
  .rounded.shadow-md.bg-white p,
  .rounded.shadow-md.bg-white ul,
  .rounded.shadow-md.bg-white li {
    text-align: left !important;
  }
`;

const BookingModule = () => {
  const [activeTab, setActiveTab] = useState("findFlights");
  const [activeService, setActiveService] = useState("flight");
  const [tripType, setTripType] = useState("roundTrip");
  const [redeemMiles, setRedeemMiles] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);

  // Inject custom styles for cross-browser select option hover
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = selectStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleServiceChange = (service) => {
    setActiveService(service);
  };

  return (
    <div className="bg-white  p-6 -mt-8 relative z-10 mx-4 md:mx-8 lg:mx-auto lg:max-w-6xl">
      {/* Main Navigation Tabs - full-width header with separators */}
      <div className="-mx-6 -mt-6 mb-6 rounded-t-lg overflow-hidden  border-gray-200">
        <div className="grid grid-cols-3 divide-x divide-gray-200">
          <button
            onClick={() => handleTabChange("findFlights")}
            className={`w-full text-left px-8 py-6 transition-colors text-aa-blue text-lg md:text-2xl ${
              activeTab === "findFlights"
                ? "bg-white"
                : "bg-aa-gray hover:bg-gray-100"
            }`}
          >
            Book
          </button>
          <button
            onClick={() => handleTabChange("manageTrips")}
            className={`w-full text-left px-8 py-6 transition-colors text-aa-blue text-lg md:text-2xl ${
              activeTab === "manageTrips"
                ? "bg-white"
                : "bg-aa-gray hover:bg-gray-100"
            }`}
          >
            Manage trips / Check-in
          </button>
          <button
            onClick={() => handleTabChange("flightStatus")}
            className={`w-full text-left px-8 py-6 transition-colors text-aa-blue text-lg md:text-2xl ${
              activeTab === "flightStatus"
                ? "bg-white"
                : "bg-aa-gray hover:bg-gray-100"
            }`}
          >
            Flight status
          </button>
        </div>
      </div>

      {/* Service Type Selection */}
      {activeTab === "findFlights" && (
        <div className="flex items-center mb-6">
          <button
            onClick={() => handleServiceChange("flight")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeService === "flight"
                ? "bg-aa-blue text-white"
                : "text-gray-600 hover:text-aa-blue"
            }`}
          >
            Flight
          </button>
          <button
            onClick={() => handleServiceChange("vacation")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ml-4 ${
              activeService === "vacation"
                ? "bg-aa-blue text-white"
                : "bg-gray-300 text-gray-800 hover:text-aa-blue"
            }`}
          >
            Vacation packages
          </button>
          <a
            href="/hotels"
            className="text-aa-blue text-sm ml-4 flex items-center"
          >
            Hotel <span className="text-xs ml-1">↗</span>
          </a>
          <a
            href="/cars"
            className=" text-aa-blue text-sm ml-4 flex items-center"
          >
            Car <span className="text-xs ml-1">↗</span>
          </a>
          <a
            href="/experiences"
            className="text-aa-blue text-sm ml-4 flex items-center"
          >
            Experiences <span className="text-xs ml-1">»</span>
          </a>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "findFlights" && (activeService === "flight" || activeService === "vacation") && (
        <FlightSearch
          tripType={tripType}
          setTripType={setTripType}
          redeemMiles={redeemMiles}
          setRedeemMiles={setRedeemMiles}
          adults={adults}
          setAdults={setAdults}
          children={children}
          setChildren={setChildren}
          activeService={activeService}
        />
      )}
      {activeTab === "manageTrips" && <TripManagement />}
      {activeTab === "flightStatus" && <FlightStatus />}
      <div className="mt-8">
        <img src="/content/images/homepage/hero/en_US/shadow-down.png" alt="" className="w-full block" />
      </div>
    </div>
  );
};

// Airport/City Search Modal
const CitySearchModal = ({ isOpen, onClose, onSelect }) => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  
  // Get unique countries from cities array
  const countries = useMemo(() => {
    const uniqueCountries = [...new Set(cities.map(city => city.country))];
    return uniqueCountries.sort();
  }, []);

  // Get unique states for USA
  const usaStates = useMemo(() => {
    if (selectedCountry !== "USA") return [];
    const uniqueStates = [...new Set(cities
      .filter(city => city.country === "USA" && city.state)
      .map(city => city.state))];
    return uniqueStates.sort();
  }, [selectedCountry]);

  // Filter airports based on selected country and state
  const airports = useMemo(() => {
    if (!selectedCountry) return [];
    
    let filteredCities = cities.filter(city => city.country === selectedCountry);
    
    // If USA is selected and a state is chosen, filter by state
    if (selectedCountry === "USA" && selectedState) {
      filteredCities = filteredCities.filter(city => city.state === selectedState);
    } else if (selectedCountry === "USA" && !selectedState) {
      // If USA is selected but no state chosen, don't show airports yet
      return [];
    }
    
    return filteredCities.sort((a, b) => a.city.localeCompare(b.city));
  }, [selectedCountry, selectedState]);

  // Reset state when country changes
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedState("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 ">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[32px] font-normal text-aa-blue">
                Airport lookup
              </h2>
              <p className="text-sm text-black mt-2">
                Please tell us where the airport is located.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 border-2 border-aa-blue text-aa-blue rounded-full flex items-center justify-center hover:border-aa-dark-blue hover:text-aa-dark-blue transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Country/Region and State Selectors */}
        <div className="px-6 py-4 ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Country Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Country/Region
              </label>
              <div className="relative">
                <select
                  value={selectedCountry}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-aa-blue focus:border-aa-blue"
                >
                  <option value="">Select a country/region...</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M6 8L2 4h8L6 8z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* State Selector (only shown for USA) */}
            {selectedCountry === "USA" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select State
                </label>
                <div className="relative">
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-aa-blue focus:border-aa-blue"
                  >
                    <option value="">Choose a state...</option>
                    {usaStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Airports Table */}
        <div className="flex-1 overflow-hidden">
          {airports.length > 0 ? (
            <div className="px-6 py-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Airports in {selectedCountry === "USA" ? `${selectedState}, ${selectedCountry}` : selectedCountry}
              </h4>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                        City
                      </th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                        Airport
                      </th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                        Code
                      </th>
                      {selectedCountry === "USA" && (
                        <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                          State
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {airports.map((airport, index) => (
                      <tr
                        key={`${airport.code}-${airport.city}`}
                        className={`transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-gray-100`}
                      >
                        <td className="px-3 py-2 text-sm border-b border-gray-200">
                          {airport.city}
                        </td>
                        <td className="px-3 py-2 text-sm border-b border-gray-200">
                          {airport.airport || '-'}
                        </td>
                        <td className="px-3 py-2 text-sm border-b border-gray-200">
                          <button
                            onClick={() => {
                              onSelect(airport);
                              onClose();
                            }}
                            className="font-semibold text-aa-blue underline hover:text-aa-dark-blue cursor-pointer transition-colors"
                          >
                            {airport.code}
                          </button>
                        </td>
                        {selectedCountry === "USA" && (
                          <td className="px-3 py-2 text-sm border-b border-gray-200">
                            {airport.state || '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : selectedCountry === "USA" && !selectedState ? (
            <div className="px-6 py-12 text-center text-gray-500">
              Please select a state to view airports
            </div>
          ) : selectedCountry && airports.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No airports found
            </div>
          ) : null}
        </div>
        
        {/* Bottom right close button */}
        <div className="flex justify-end p-4">
          <button
            onClick={onClose}
            className="w-48 h-10 bg-aa-blue text-white px-5 mr-6  rounded-sm hover:bg-aa-dark-blue transition-colors font-medium text-base"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Simple reusable autocomplete for airport/city selection
const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  placeholder = "City or airport",
  hasActiveBorder = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return [];
    return cities
      .filter((c) => {
        const inKeywords = (c.keywords || []).some((k) =>
          k.toLowerCase().includes(q)
        );
        const inText =
          `${c.city} ${c.airport} ${c.state} ${c.country} ${c.code}`
            .toLowerCase()
            .includes(q);
        return inKeywords || inText;
      })
      .slice(0, 12);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          setIsOpen(true);
        }}
        onKeyDown={(e) => {
          if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setIsOpen(true);
            return;
          }
          if (filtered.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % filtered.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) =>
              prev <= 0 ? filtered.length - 1 : prev - 1
            );
          } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            const opt = filtered[activeIndex];
            onSelect(opt);
            setIsOpen(false);
          } else if (e.key === "Escape") {
            setIsOpen(false);
          }
        }}
        placeholder={placeholder}
        className="w-full h-10 px-3 border border-gray-300 placeholder-gray-500 text-sm focus:outline-none focus:border-2 focus:border-aa-blue"
      />
      {isOpen && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-sm shadow max-h-80 overflow-auto">
          {filtered.map((opt, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={`${opt.code}-${opt.city}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(-1)}
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 hover:bg-aa-blue ${
                  isActive ? "bg-aa-blue" : "bg-white"
                }`}
              >
                <div
                  className={`text-sm font-semibold whitespace-nowrap truncate leading-5 ${
                    isActive ? "text-white" : "text-aa-blue hover:text-white"
                  }`}
                >
                  {opt.display}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Date utilities
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, n) =>
  new Date(date.getFullYear(), date.getMonth() + n, 1);
const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isBefore = (a, b) =>
  a && b && a.setHours(0, 0, 0, 0) < b.setHours(0, 0, 0, 0);
const isAfter = (a, b) =>
  a && b && a.setHours(0, 0, 0, 0) > b.setHours(0, 0, 0, 0);
const inRange = (d, start, end) => {
  if (!start || !end) return false;
  const t = d.setHours(0, 0, 0, 0);
  const s = start.setHours(0, 0, 0, 0);
  const e = end.setHours(0, 0, 0, 0);
  return t >= s && t <= e;
};
const formatDdMmYyyy = (d) => {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatMmDdYy = (d) => {
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const parseDateInput = (input) => {
  if (!input || typeof input !== 'string') return null;
  
  const cleaned = input.replace(/[^\d\/]/g, '');
  const parts = cleaned.split('/');
  
  // Must have exactly 3 parts (month/day/year)
  if (parts.length !== 3) return null;
  
  // All parts must have content and be valid numbers
  if (parts[0] === '' || parts[1] === '' || parts[2] === '') return null;
  
  let month, day, year;
  
  if (parts[0].length <= 2 && parts[1].length <= 2) {
    month = parseInt(parts[0], 10);
    day = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
    
    // Return null for invalid numbers
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    
    // Year must be at least 2 digits when typed (10-99 for 2-digit years)
    if (parts[2].length === 1) return null;
    
    if (year < 100) {
      year += year < 50 ? 2000 : 1900;
    }
  } else {
    return null;
  }
  
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  
  const date = new Date(year, month - 1, day);
  
  if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
    return null;
  }
  
  return date;
};

const weekdays = ["S", "M", "T", "W", "T", "F", "S"];
const monthLabel = (date) =>
  date.toLocaleString("en-US", { month: "long", year: "numeric" });

const MonthGrid = ({ monthDate, startDate, endDate, onSelectDay, editingField = 'depart' }) => {
  const first = startOfMonth(monthDate);
  const startWeekday = first.getDay(); // Sunday as 0
  const daysInMonth = new Date(
    first.getFullYear(),
    first.getMonth() + 1,
    0
  ).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), d));
  }
  // Fill to 6 rows * 7 cols = 42
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="px-2 pb-2">
      <div className="grid grid-cols-7 text-center text-gray-600 text-xs mb-1">
        {weekdays.map((w) => (
          <div key={w} className="py-1 font-bold">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center">
        {cells.map((date, idx) => {
          if (!date) return <div key={idx} className="h-8"></div>;
          const selectedStart = startDate && isSameDay(date, startDate);
          const selectedEnd = endDate && isSameDay(date, endDate);
          const inSelRange = inRange(
            new Date(date),
            startDate ? new Date(startDate) : null,
            endDate ? new Date(endDate) : null
          );
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const currentDate = new Date(date);
          currentDate.setHours(0, 0, 0, 0);
          
          const isToday = isSameDay(date, new Date());
          const isPast = currentDate < today;
          const isBeforeDeparture = editingField === 'return' && startDate && isBefore(new Date(date), new Date(startDate));
          const isDisabled = isPast || isBeforeDeparture;
          
          // Determine background styling for continuous range
          let bgClass = "";
          if ((selectedStart || selectedEnd || inSelRange) && !isDisabled) {
            if (selectedStart && selectedEnd && isSameDay(startDate, endDate)) {
              // Single day selection (same start and end date)
              bgClass = "bg-[#00467f] rounded-sm";
            } else if (selectedStart) {
              // Start of range
              bgClass = "bg-[#00467f] rounded-l-sm";
            } else if (selectedEnd) {
              // End of range
              bgClass = "bg-[#00467f] rounded-r-sm";
            } else if (inSelRange) {
              // Middle of range
              bgClass = "bg-[#00467f]";
            }
          }
          
          return (
            <button
              key={idx}
              type="button"
              onClick={() => !isDisabled && onSelectDay(date)}
              disabled={isDisabled}
              className={`group relative h-8 py-1 ${
                isDisabled 
                  ? "cursor-default" 
                  : inSelRange ? "hover:border hover:border-white" : "hover:bg-blue-50"
              } ${bgClass} ${
                (selectedStart || selectedEnd || inSelRange) && !isDisabled ? "text-white" : "text-[#00467f]"
              }`}
            >
              <span
                className={`inline-flex h-5 w-5 items-center justify-center text-xs ${
                  isDisabled
                    ? "line-through"
                    : ""
                } ${
                  selectedStart || selectedEnd
                    ? "font-bold"
                    : ""
                } ${
                  isToday && !inSelRange && !isDisabled
                    ? "ring-1 ring-aa-blue rounded-full"
                    : ""
                } ${
                  !isDisabled ? (selectedStart || selectedEnd || inSelRange) ? "hover:underline hover:decoration-white" : "hover:underline hover:decoration-[#00467f]" : ""
                }`}
              >
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateRangePicker = ({ startDate, endDate, onChange, onClose, editingField = 'depart' }) => {
  const [baseMonth, setBaseMonth] = useState(
    startOfMonth(startDate || new Date())
  );
  const containerRef = useRef(null);
  
  const currentMonth = startOfMonth(new Date());
  const isCurrentMonth = baseMonth.getTime() === currentMonth.getTime();

  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const handleSelectDay = (date) => {
    if (editingField === 'depart') {
      onChange({ start: date, end: endDate });
      onClose();
    } else if (editingField === 'return') {
      if (!startDate) {
        onChange({ start: date, end: null });
        onClose();
      } else if (isBefore(new Date(date), new Date(startDate))) {
        onChange({ start: date, end: startDate });
        onClose();
      } else {
        onChange({ start: startDate, end: date });
        onClose();
      }
    } else {
      if (!startDate || (startDate && endDate)) {
        onChange({ start: date, end: null });
        onClose();
        return;
      }
      if (startDate && !endDate) {
        if (isBefore(new Date(date), new Date(startDate))) {
          onChange({ start: date, end: startDate });
        } else {
          onChange({ start: startDate, end: date });
        }
        onClose();
      }
    }
  };

  const left = baseMonth;
  const right = addMonths(baseMonth, 1);

  return (
    <div
      ref={containerRef}
      className="bg-white border border-gray-300 shadow-lg rounded-sm"
    >
      <div className="flex">
        {/* Left Month */}
        <div className="w-[230px]">
          <div className="bg-gradient-to-t from-aa-dark-blue to-aa-blue text-white px-3 py-2 text-sm font-semibold flex items-center">
            <button
              type="button"
              className={`p-1 mr-2 ${isCurrentMonth ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}
              onClick={() => !isCurrentMonth && setBaseMonth(addMonths(baseMonth, -1))}
              disabled={isCurrentMonth}
            >
            {isCurrentMonth?<p className="h-4 w-4"></p> :<svg
                className="h-4 w-4 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M12.8 15.8a1 1 0 01-1.4 0l-5-5a1 1 0 010-1.4l5-5a1 1 0 111.4 1.4L8.41 10l4.39 4.4a1 1 0 010 1.4z" />
              </svg>}
            </button>
            <span className="flex-1 text-center text-xs">{monthLabel(left)}</span>
          </div>
          <MonthGrid
            monthDate={left}
            startDate={startDate}
            endDate={endDate}
            onSelectDay={handleSelectDay}
            editingField={editingField}
          />
        </div>
        {/* Right Month */}
        <div className="w-[230px] border-l border-gray-200">
          <div className="bg-gradient-to-t from-aa-dark-blue to-aa-blue text-white px-3 py-2 text-sm font-semibold flex items-center">
            <span className="flex-1 text-center text-xs">{monthLabel(right)}</span>
            <button
              type="button"
              className="p-1 ml-2 hover:opacity-80"
              onClick={() => setBaseMonth(addMonths(baseMonth, 1))}
            >
              <svg
                className="h-4 w-4 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M7.2 4.2a1 1 0 011.4 0l5 5a1 1 0 010 1.4l-5 5a1 1 0 11-1.4-1.4L11.59 10 7.2 5.6a1 1 0 010-1.4z" />
              </svg>
            </button>
          </div>
          <MonthGrid
            monthDate={right}
            startDate={startDate}
            endDate={endDate}
            onSelectDay={handleSelectDay}
            editingField={editingField}
          />
        </div>
      </div>
      <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-aa-blue text-sm font-semibold">
        <button
          type="button"
          onClick={() => onChange({ start: new Date(), end: null })}
        >
          Today
        </button>
        <button type="button" onClick={onClose} className="">
          close
        </button>
      </div>
    </div>
  );
};

const FlightSearch = ({
  tripType,
  setTripType,
  redeemMiles,
  setRedeemMiles,
  adults,
  setAdults,
  children,
  setChildren,
  activeService,
}) => {
  const navigate = useNavigate();
  const [fromQuery, setFromQuery] = useState("JFK");
  const [toQuery, setToQuery] = useState("LAX");
  const [departDate, setDepartDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [editingField, setEditingField] = useState('depart');
  const [departInput, setDepartInput] = useState("");
  const [returnInput, setReturnInput] = useState("");
  const [farePreference, setFarePreference] = useState("coach");
  const [isManuallyEditing, setIsManuallyEditing] = useState({ depart: false, return: false });
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchModalField, setSearchModalField] = useState(null);
  const [activeSearchField, setActiveSearchField] = useState(null);
  const pickerRef = useRef(null);
  useEffect(() => {
    if (tripType === "oneWay") {
      setReturnDate(null);
      setReturnInput("");
    }
  }, [tripType]);

  useEffect(() => {
    if (!isManuallyEditing.depart) {
      if (departDate) {
        setDepartInput(formatMmDdYy(departDate));
      } else {
        setDepartInput("");
      }
    }
  }, [departDate, isManuallyEditing.depart]);

  useEffect(() => {
    if (!isManuallyEditing.return) {
      if (returnDate) {
        setReturnInput(formatMmDdYy(returnDate));
      } else {
        setReturnInput("");
      }
    }
  }, [returnDate, isManuallyEditing.return]);

  const handleDepartInputChange = (value) => {
    setIsManuallyEditing(prev => ({ ...prev, depart: true }));
    setDepartInput(value);
    if (!value.trim()) {
      setDepartDate(null);
      setIsManuallyEditing(prev => ({ ...prev, depart: false }));
    } else {
      const parsed = parseDateInput(value);
      if (parsed) {
        setDepartDate(parsed);
        setIsManuallyEditing(prev => ({ ...prev, depart: false }));
      }
      // If parsing fails, keep manual editing flag true to prevent useEffect override
    }
  };

  const handleReturnInputChange = (value) => {
    setIsManuallyEditing(prev => ({ ...prev, return: true }));
    setReturnInput(value);
    if (!value.trim()) {
      setReturnDate(null);
      setIsManuallyEditing(prev => ({ ...prev, return: false }));
    } else {
      const parsed = parseDateInput(value);
      if (parsed) {
        setReturnDate(parsed);
        setIsManuallyEditing(prev => ({ ...prev, return: false }));
      }
      // If parsing fails, keep manual editing flag true to prevent useEffect override
    }
  };

  const handleDepartCalendarClick = () => {
    setEditingField('depart');
    setPickerOpen(true);
  };

  const handleReturnCalendarClick = () => {
    setEditingField('return');
    setPickerOpen(true);
  };

  const handleSearchIconClick = (fieldType) => {
    setSearchModalField(fieldType);
    setSearchModalOpen(true);
    setActiveSearchField(fieldType);
  };

  const handleCitySelect = (city) => {
    if (searchModalField === 'from') {
      setFromQuery(city.code);
    } else if (searchModalField === 'to') {
      setToQuery(city.code);
    }
    setActiveSearchField(null);
  };
  return (
    <div>
      {/* Trip Type and Redeem Miles */}
      <div className="flex flex-wrap items-center mb-4 gap-8">
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-8">
            <label className="flex items-center space-x-3 text-base text-gray-800">
              <input
                type="radio"
                name="tripType"
                value="roundTrip"
                checked={tripType === "roundTrip"}
                onChange={(e) => setTripType(e.target.value)}
                className="h-5 w-5 accent-aa-blue border-2 border-gray-300 focus:ring-0"
              />
              <span className="font-medium">Round trip</span>
            </label>
            <label className="flex items-center space-x-3 text-base text-gray-800">
              <input
                type="radio"
                name="tripType"
                value="oneWay"
                checked={tripType === "oneWay"}
                onChange={(e) => setTripType(e.target.value)}
                className="h-5 w-5 accent-aa-blue border-2 border-gray-300 focus:ring-0"
              />
              <span className="font-medium">One way</span>
            </label>
          </div>
        </div>

        <div className="flex items-center">
          <label className="flex items-center space-x-3 text-base text-gray-800">
            <input
              type="checkbox"
              checked={redeemMiles}
              onChange={(e) => setRedeemMiles(e.target.checked)}
              className="h-5 w-5 accent-aa-blue rounded-[3px] border-2 border-gray-300 focus:ring-0"
            />
            <span className="font-medium">Redeem Miles</span>
          </label>
        </div>
      </div>

      {/* Required Fields Note */}
      {/* <div className="mb-4">
        <small className="text-gray-600">
          (<span className="text-red-500">•</span> Required)
        </small>
      </div> */}

      {/* Flight Search Form */}
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        
        // Validate form fields
        const errors = [];
        if (!toQuery.trim()) errors.push("To");
        if (!departInput.trim()) errors.push("Depart");
        if (tripType === "roundTrip" && !returnInput.trim()) errors.push("Return");
        
        // Check if cities exist in our database
        if (fromQuery && !getCityByCode(fromQuery)) errors.push("Invalid departure city");
        if (toQuery && !getCityByCode(toQuery)) errors.push("Invalid destination city");

        if (errors.length > 0) {
          // Navigate to validation page with errors
          navigate('/validation', {
            state: {
              errors: errors,
              formData: {
                tripType,
                redeemMiles,
                adults,
                children,
                fromQuery,
                toQuery,
                departDate,
                returnDate,
                departInput,
                returnInput,
                farePreference,
                activeService
              }
            }
          });
        } else {
          // Navigate to search results if no errors
          const searchParams = new URLSearchParams({
            from: fromQuery,
            to: toQuery,
            tripType,
            departDate: departDate ? departDate.toISOString() : '',
            returnDate: returnDate ? returnDate.toISOString() : '',
            adults: adults.toString(),
            children: children.toString(),
            farePreference,
            redeemMiles: redeemMiles.toString()
          });
          navigate(`/search-results?${searchParams.toString()}`);
        }
      }}>
        {/* Row 1: From, To, Adults (align to 3/4 width so it ends before Search) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From 
            </label>
            <div className="relative">
              <AutocompleteInput
                value={fromQuery}
                onChange={setFromQuery}
                onSelect={(opt) => setFromQuery(opt.code)}
                placeholder="City or airport"
                hasActiveBorder={false}
              />
              <button
                type="button"
                onClick={() => handleSearchIconClick('from')}
                className={`absolute inset-y-0 right-0 flex items-center pr-3 rounded-r ${
                  activeSearchField === 'from' 
                    ? 'border-2 border-aa-blue bg-blue-50' 
                    : 'hover:bg-blue-50'
                }`}
              >
                <svg
                  className="h-4 w-4 text-aa-blue rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To 
            </label>
            <div className="relative">
              <AutocompleteInput
                value={toQuery}
                onChange={setToQuery}
                onSelect={(opt) => setToQuery(opt.code)}
                placeholder="City or airport"
                hasActiveBorder={false}
              />
              <button
                type="button"
                onClick={() => handleSearchIconClick('to')}
                className={`absolute inset-y-0 right-0 flex items-center pr-3 rounded-r ${
                  activeSearchField === 'to' 
                    ? 'border-2 border-aa-blue bg-blue-50' 
                    : 'hover:bg-blue-50'
                }`}
              >
                <svg
                  className="h-4 w-4 text-aa-blue rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of passengers
            </label>
            <div className="relative">
              <select
                value={adults}
                onChange={(e) => setAdults(parseInt(e.target.value))}
                className="w-full h-10 px-3 pr-12 border border-gray-300 bg-white appearance-none text-base focus:outline-none focus:border-2 focus:border-aa-blue rounded-none"
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-5 text-aa-blue"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                >
                  <path d="M6 8L2 4h8L6 8z" />
                </svg>
              </div>
            </div>
          </div>
          {/* Placeholder to align with 4th column (Search) below on md+ */}
          <div className="hidden md:block"></div>
        </div>

        {/* Row 2: Different layouts for flight vs vacation */}
        {activeService === "vacation" ? (
          <>
            {/* Vacation packages: Depart, Return, Fare preference in 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Depart
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={departInput}
                    onChange={(e) => handleDepartInputChange(e.target.value)}
                    placeholder="mm/dd/yy"
                    className="w-full h-10 px-3 pr-8 border border-gray-300 placeholder-gray-500 text-sm focus:outline-none focus:border-2 focus:border-aa-blue"
                  />
                  <button
                    type="button"
                    onClick={handleDepartCalendarClick}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    <svg
                      className="h-4 w-4 text-[#0078d2]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  {isPickerOpen && editingField === 'depart' && (
                    <div ref={pickerRef} className="absolute right-0 bottom-full mb-1 z-50">
                      <DateRangePicker
                        startDate={departDate}
                        endDate={tripType === "roundTrip" ? returnDate : null}
                        editingField={editingField}
                        onChange={({ start, end }) => {
                          setDepartDate(start);
                          setReturnDate(tripType === "roundTrip" ? end : null);
                          setIsManuallyEditing({ depart: false, return: false });
                        }}
                        onClose={() => setPickerOpen(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="relative">
                <label
                  className={`block text-sm font-medium text-gray-700 mb-1 ${
                    tripType === "oneWay" ? "invisible" : ""
                  }`}
                >
                  Return
                </label>
                <div
                  className={`relative ${tripType === "oneWay" ? "invisible" : ""}`}
                >
                  <input
                    type="text"
                    value={returnInput}
                    onChange={(e) => handleReturnInputChange(e.target.value)}
                    placeholder="mm/dd/yy"
                    disabled={tripType === "oneWay"}
                    className="w-full h-10 px-3 pr-8 border border-gray-300 placeholder-gray-500 text-sm focus:outline-none focus:border-2 focus:border-aa-blue disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={handleReturnCalendarClick}
                    disabled={tripType === "oneWay"}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="h-4 w-4 text-[#0078d2]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  {isPickerOpen && editingField === 'return' && (
                    <div ref={pickerRef} className="absolute right-0 bottom-full mb-1 z-50">
                      <DateRangePicker
                        startDate={departDate}
                        endDate={tripType === "roundTrip" ? returnDate : null}
                        editingField={editingField}
                        onChange={({ start, end }) => {
                          setDepartDate(start);
                          setReturnDate(tripType === "roundTrip" ? end : null);
                          setIsManuallyEditing({ depart: false, return: false });
                        }}
                        onClose={() => setPickerOpen(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fare preference
                </label>
                <div className="relative">
                  <select
                    value={farePreference}
                    onChange={(e) => setFarePreference(e.target.value)}
                    className="w-full h-10 px-3 pr-12 border border-gray-300 bg-white appearance-none text-base focus:outline-none focus:border-2 focus:border-aa-blue rounded-none"
                  >
                    <option value="coach">Coach</option>
                    <option value="business">Business</option>
                    <option value="first">First</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="h-4 w-5 text-aa-blue"
                      fill="currentColor"
                      viewBox="0 0 12 12"
                      aria-hidden="true"
                    >
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                {/* Search button below fare preference */}
                <div className="mt-4">
                  <button
                    type="submit"
                    className="w-full h-10 bg-aa-blue text-white px-5 rounded-sm hover:bg-aa-dark-blue transition-colors font-medium text-base"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Flight: Original layout with Depart, Return, Search in 3 columns */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Depart
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={departInput}
                  onChange={(e) => handleDepartInputChange(e.target.value)}
                  placeholder="mm/dd/yy"
                  className="w-full h-10 px-3 pr-8 border border-gray-300 placeholder-gray-500 text-sm focus:outline-none focus:border-2 focus:border-aa-blue"
                />
                <button
                  type="button"
                  onClick={handleDepartCalendarClick}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <svg
                    className="h-4 w-4 text-[#0078d2]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                {isPickerOpen && editingField === 'depart' && (
                  <div ref={pickerRef} className="absolute right-0 bottom-full mb-1 z-50">
                    <DateRangePicker
                      startDate={departDate}
                      endDate={tripType === "roundTrip" ? returnDate : null}
                      editingField={editingField}
                      onChange={({ start, end }) => {
                        setDepartDate(start);
                        setReturnDate(tripType === "roundTrip" ? end : null);
                      }}
                      onClose={() => setPickerOpen(false)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <label
                className={`block text-sm font-medium text-gray-700 mb-1 ${
                  tripType === "oneWay" ? "invisible" : ""
                }`}
              >
                Return
              </label>
              <div
                className={`relative ${tripType === "oneWay" ? "invisible" : ""}`}
              >
                <input
                  type="text"
                  value={returnInput}
                  onChange={(e) => handleReturnInputChange(e.target.value)}
                  placeholder="mm/dd/yy"
                  disabled={tripType === "oneWay"}
                  className="w-full h-10 px-3 pr-8 border border-gray-300 placeholder-gray-500 text-sm focus:outline-none focus:border-2 focus:border-aa-blue disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={handleReturnCalendarClick}
                  disabled={tripType === "oneWay"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 disabled:cursor-not-allowed"
                >
                  <svg
                    className="h-4 w-4 text-[#0078d2]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                {isPickerOpen && editingField === 'return' && (
                  <div ref={pickerRef} className="absolute right-0 bottom-full mb-1 z-50">
                    <DateRangePicker
                      startDate={departDate}
                      endDate={tripType === "roundTrip" ? returnDate : null}
                      editingField={editingField}
                      onChange={({ start, end }) => {
                        setDepartDate(start);
                        setReturnDate(tripType === "roundTrip" ? end : null);
                      }}
                      onClose={() => setPickerOpen(false)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full h-10 bg-aa-blue text-white px-5 rounded-sm hover:bg-aa-dark-blue transition-colors font-medium text-base"
              >
                Search
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Bag and Optional Fees - Left Side & Advanced Search Link - Right Side */}
      <div className="mt-4 flex justify-between items-start">
        <div className="space-y-2">
          <p>
            <a 
              href="/i18n/customer-service/support/optional-service-fees.jsp" 
              className="text-aa-blue hover:text-aa-dark-blue hover:underline text-sm font-medium transition-colors"
            >
              Bag and optional fees »
            </a>
          </p>
          <p>
            <a 
              href="/i18n/travel-info/baggage/checked-baggage-policy.jsp" 
              className="text-aa-blue hover:text-aa-dark-blue hover:underline text-sm font-medium transition-colors"
            >
              Changes to checked bag fees and allowances »
            </a>
          </p>
        </div>
        {activeService !== "vacation" && (
          <div>
            <a
              href="/advanced-search"
              className="text-aa-blue hover:underline text-sm"
            >
              Advanced / Multi-city search »
            </a>
          </div>
        )}
      </div>

      {/* City Search Modal */}
      <CitySearchModal
        isOpen={searchModalOpen}
        onClose={() => {
          setSearchModalOpen(false);
          setActiveSearchField(null);
        }}
        onSelect={handleCitySelect}
      />
    </div>
  );
};

const TripManagement = () => {
  const [activeTab, setActiveTab] = useState("findTrip");

  return (
    <div>
      {/* Segmented buttons */}
      <div className="flex mb-6">
        <button
          onClick={() => setActiveTab("findTrip")}
          className={`px-8 py-1 text-sm font-medium transition-colors border ${
            activeTab === "findTrip"
              ? "bg-aa-blue text-white border-aa-blue"
              : "bg-white text-aa-blue border-gray-300 hover:border-aa-blue"
          }`}
        >
          Find trip
        </button>
        <button
          onClick={() => setActiveTab("findTravelCredit")}
          className={`px-6 py-1 text-sm font-medium transition-colors border-t border-b border-r ${
            activeTab === "findTravelCredit"
              ? "bg-aa-blue text-white border-aa-blue"
              : "bg-white text-aa-blue border-gray-300 hover:border-aa-blue"
          }`}
        >
          Find travel credit
        </button>
      </div>

      {/* Information message for find travel credit */}
      {activeTab === "findTravelCredit" && (
        <div className="mb-6 p-4">
          <p className="text-xl text-gray-500">
            Enter the 13-digit travel credit number (begins with '00115' or '0012') or ticket number (begins with '001').
          </p>
        </div>
      )}


      {activeTab === "findTrip" && (
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                 Last name 
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Last name or names"
                  className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-9 h-9 -mt-px text-aa-blue bg-transparent group"
                  aria-label="Information about last name"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-300 rounded shadow-md p-2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-150">
                    <div className="text-xs">
                      <p className="tooltip-heading tool-tip font-medium text-gray-900 mb-2">Last name</p>
                      <ul>
                        <li className="tool-tip-text list-bullet text-gray-600 leading-tight">Enter last name or names without special characters (-,/,etc. are not allowed)</li>
                        <li className="tool-tip-text list-bullet text-gray-600 leading-tight">If you have multiple last names, enter them the same as when you booked (spaces are OK, but leave off any hyphens).</li>
                      </ul>
                    </div>
                    <div className="absolute -top-1 right-3 w-2 h-2 bg-white border-l border-t border-gray-300 transform rotate-45"></div>
                  </div>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of birth (for your security)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Month</option>
                    <option value="01">Jan</option>
                    <option value="02">Feb</option>
                    <option value="03">Mar</option>
                    <option value="04">Apr</option>
                    <option value="05">May</option>
                    <option value="06">Jun</option>
                    <option value="07">Jul</option>
                    <option value="08">Aug</option>
                    <option value="09">Sep</option>
                    <option value="10">Oct</option>
                    <option value="11">Nov</option>
                    <option value="12">Dec</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Year</option>
                    {Array.from({ length: 100 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmation code -or- ticket number <span className="text-red-500">•</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ex. JCQNHD -or- 0012345678900"
                  className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-9 h-9 -mt-px text-aa-blue bg-transparent group"
                  aria-label="Information about confirmation code or ticket number"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="absolute right-[-8px] top-full mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-150">
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900 mb-2">Trip Credit</p>
                      <p className="text-gray-600 mb-3 leading-relaxed">
                        Check your email or your AAdvantage® account for your 13-digit Trip Credit number that begins with '00115'.
                      </p>
                      <p className="font-semibold text-gray-900 mb-2">Ticket number</p>
                      <p className="text-gray-600 leading-relaxed">
                        Check your email for your 13-digit ticket number that begins with '001'.
                      </p>
                    </div>
                    <div className="absolute -top-1 right-6 w-2 h-2 bg-white border-l border-t border-gray-300 transform rotate-45"></div>
                  </div>
                </button>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-aa-blue text-white py-2 px-6 rounded-md hover:bg-aa-dark-blue transition-colors font-medium"
                >
                  Find trip
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {activeTab === "findTravelCredit" && (
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last name <span className="text-red-500">•</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-9 h-9 -mt-px text-aa-blue bg-transparent group"
                  aria-label="Information about last name"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-300 rounded shadow-md p-2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-150">
                    <div className="text-xs">
                      <p className="tooltip-heading tool-tip font-medium text-gray-900 mb-2">Last name</p>
                      <ul>
                        <li className="tool-tip-text list-bullet text-gray-600 leading-tight">Enter last name or names without special characters (-,/,etc. are not allowed)</li>
                        <li className="tool-tip-text list-bullet text-gray-600 leading-tight">If you have multiple last names, enter them the same as when you booked (spaces are OK, but leave off any hyphens).</li>
                      </ul>
                    </div>
                    <div className="absolute -top-1 right-3 w-2 h-2 bg-white border-l border-t border-gray-300 transform rotate-45"></div>
                  </div>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of birth (for your security)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Month</option>
                    <option value="01">Jan</option>
                    <option value="02">Feb</option>
                    <option value="03">Mar</option>
                    <option value="04">Apr</option>
                    <option value="05">May</option>
                    <option value="06">Jun</option>
                    <option value="07">Jul</option>
                    <option value="08">Aug</option>
                    <option value="09">Sep</option>
                    <option value="10">Oct</option>
                    <option value="11">Nov</option>
                    <option value="12">Dec</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue appearance-none">
                    <option value="">Year</option>
                    {Array.from({ length: 100 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Travel credit number or ticket number <span className="text-red-500">•</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ex. 0012345678900"
                  className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aa-blue"
                />
                <button
                  type="button"
                  className="group absolute inset-y-0 right-0 flex items-center justify-center w-9 h-9 -mt-px text-aa-blue bg-transparent"
                  aria-label="Information about travel credit number or ticket number"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="absolute right-[-8px] top-full mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-150">
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900 mb-2">Trip Credit</p>
                      <p className="text-gray-600 mb-3 leading-relaxed">
                        Check your email or your AAdvantage® account for your 13-digit Trip Credit number that begins with '00115'.
                      </p>
                      <p className="font-semibold text-gray-900 mb-2">Ticket number</p>
                      <p className="text-gray-600 leading-relaxed">
                        Check your email for your 13-digit ticket number that begins with '001'.
                      </p>
                    </div>
                    <div className="absolute -top-1 right-6 w-2 h-2 bg-white border-l border-t border-gray-300 transform rotate-45"></div>
                  </div>
                </button>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-aa-blue text-white py-2 px-6 rounded-md hover:bg-aa-dark-blue transition-colors font-medium"
                >
                  Find travel credit
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {/* <div className="mt-4 text-right">
        <small className="text-gray-600">
          You will be redirected to the US aa.com website
        </small>
      </div> */}
    </div>
  );
};

const FlightStatus = () => {
  const [searchBy, setSearchBy] = useState("cities");

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center mb-4">
          <span className="text-sm font-medium mr-4">Search by:</span>
          <button
            onClick={() => setSearchBy("cities")}
            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ${
              searchBy === "cities"
                ? "bg-aa-blue text-white underline"
                : "text-gray-600 hover:text-aa-blue"
            }`}
          >
            Cities
          </button>
          <button
            onClick={() => setSearchBy("flightNumber")}
            className={`px-4 py-1 rounded-full text-sm font-medium transition-colors ml-2 ${
              searchBy === "flightNumber"
                ? "bg-aa-blue text-white underline"
                : "text-gray-600 hover:text-aa-blue"
            }`}
          >
            Flight number
          </button>
        </div>

        <div className="mb-4">
          <small className="text-gray-600">
            (<span className="text-red-500">•</span> Required)
          </small>
        </div>
      </div>

      <form className="space-y-4">
        {searchBy === "cities" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From <span className="text-red-500">•</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="City or airport"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 focus:outline-none focus:border-2 focus:border-aa-blue"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="h-4 w-4 text-aa-blue rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To <span className="text-red-500">•</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="City or airport"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 focus:outline-none focus:border-2 focus:border-aa-blue"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="h-4 w-4 text-aa-blue rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 bg-white appearance-none focus:outline-none focus:border-2 focus:border-aa-blue">
                    <option>Monday, August 18</option>
                    <option>Tuesday, August 19</option>
                    <option>Wednesday, August 20</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    className="w-full bg-aa-blue text-white py-2 px-6 rounded-md hover:bg-aa-dark-blue transition-colors font-medium"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flight number <span className="text-red-500">•</span>
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-2 focus:border-aa-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <div className="relative">
                  <select className="w-full px-3 py-2 pr-10 border border-gray-300 bg-white appearance-none focus:outline-none focus:border-2 focus:border-aa-blue">
                    <option>Monday, August 18</option>
                    <option>Tuesday, August 19</option>
                    <option>Wednesday, August 20</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-4 w-5 text-aa-blue" fill="currentColor" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 8L2 4h8L6 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                {/* Empty third column */}
              </div>
            </div>
            {/* Search button in new row, 3rd column position */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div></div>
              <div></div>
              <div>
                <button
                  type="submit"
                  className="w-full bg-aa-blue text-white py-2 px-6 rounded-md hover:bg-aa-dark-blue transition-colors font-medium"
                >
                  Search
                </button>
              </div>
            </div>
          </>
        )}
      </form>

      {/* <div className="mt-4 text-right">
        <small className="text-gray-600">
          You will be redirected to the US aa.com website
        </small>
      </div> */}
    </div>
  );
};

export default BookingModule;
